import { EventEmitter } from "events";
import EditQuery from "./VoicevoxUtils";
import { defaultSpeakerInfo } from "../../vrm_utils/VRMUtils";
import { SpeakerInfo, Query } from "./VoicevoxUtils";
import { api } from "../../api";
import { audioManager, tryPlayAudio } from "~/SoundUtils";
import { removeDollarKeys } from "~/utils/chatMessageUtils";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import { t } from "i18next";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";

const isAWS = process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws";

export const emitter = new EventEmitter();

// 音声追跡用のグローバル変数
let lastAudioId = 0;
let currentPlayingAudioId: number | null = null;

/**
 * VOICEVOXを使用してテキストを音声合成し再生する
 *
 * @param inputText 音声合成するテキスト
 * @param setIsThinking 思考中フラグの更新関数
 * @param setIsProcessing 処理中フラグの更新関数
 * @param setIsTranscribing 文字起こし有効フラグの更新関数
 * @param setOutputText 出力テキストの更新関数
 * @param speakerInfo VOICEVOX話者情報
 * @param setIsInConversationCycle 会話サイクルフラグの更新関数
 * @param endExpression 再生終了時にemitterで通知する表情（デフォルト: "neutral"）。
 *                      "bow" を指定すると発話終了後にお辞儀モーションが再生される。
 */
const handleSynthesize = async (
  inputText: string,
  setIsThinking?: (flag: boolean) => void,
  setIsProcessing?: (flag: boolean) => void,
  setIsTranscribing?: (flag: boolean) => void,
  setOutputText?: (text: string) => void,
  speakerInfo: SpeakerInfo = defaultSpeakerInfo,
  setIsInConversationCycle?: (flag: boolean) => void,
  endExpression: string = "neutral",
  playbackToken?: number
  // eslint-disable-next-line complexity
) => {
  const getLabData = (jsondata: Query): string => {
    let labdata = "";
    let nowLength = 0;
    const timescale = 10000000 / jsondata.speedScale;

    jsondata.accent_phrases.forEach((phrase) => {
      phrase.moras.forEach((mora) => {
        if (mora.consonant_length !== null) {
          labdata += `${Math.round(nowLength * timescale)} `;
          nowLength += mora.consonant_length;
          labdata += `${Math.round(nowLength * timescale)} ${mora.consonant}\n`;
        }
        labdata += `${Math.round(nowLength * timescale)} `;
        nowLength += mora.vowel_length;
        labdata += `${Math.round(nowLength * timescale)} ${mora.vowel}\n`;
      });

      if (phrase.pause_mora !== null) {
        labdata += `${Math.round(nowLength * timescale)} `;
        nowLength += phrase.pause_mora.vowel_length;
        labdata += `${Math.round(nowLength * timescale)} pau\n`;
      }
    });

    return labdata;
  };

  const playAndGetLabData = (labData: string, audio: HTMLAudioElement) => {
    // 各音声に一意のIDを割り当て
    const audioId = ++lastAudioId;
    currentPlayingAudioId = audioId;

    const lines = labData.split("\n");
    let currentIndex = 0;
    let intervalId: number | null = null;

    const logFrame = () => {
      const currentTime = Math.min(audio.currentTime + 0.08, audio.duration); // 0.05秒進めるが音声の終了時間を超えない
      while (currentIndex < lines.length) {
        const [startTimeStr, endTimeStr, phoneme] = lines[currentIndex].split(" ");
        const startTime = parseFloat(startTimeStr) / 10000000; // マイクロ秒を秒に変換
        const endTime = parseFloat(endTimeStr) / 10000000; // マイクロ秒を秒に変換
        if (currentTime >= startTime && currentTime < endTime) {
          // console.log(`${currentTime} s: ${phoneme} - Start: ${startTime}, End: ${endTime}`);
          // Model.tsxのhandlePhonemeChange関数を呼び出す
          emitter.emit("phoneme", phoneme); // 発音情報を送信
          break;
        }
        if (currentTime > startTime) {
          currentIndex++;
        } else {
          break;
        }
      }
    };

    audio.addEventListener("play", () => {
      intervalId = window.setInterval(logFrame, 50); // 50msごとにlogFrameを実行
    });
    audio.addEventListener("ended", () => {
      if (intervalId !== null) {
        clearInterval(intervalId); // 音声が終了したらsetIntervalをクリア
        // setOutputText("");
      }
      // console.log("s: end");
      emitter.emit("expression", endExpression); // 表情を通知
      emitter.emit("phoneme", "end"); // 再生終了を通知
      if (setIsThinking) setIsThinking(false);
      // この音声が最新の音声か判定
      if (audioId === currentPlayingAudioId) {
        setTimeout(() => {
          // タイムアウト後も最新の音声なら文字起こしを有効化
          if (audioId === currentPlayingAudioId) {
            if (setIsTranscribing) setIsTranscribing(true);
            currentPlayingAudioId = null;
          }
        }, 1500);
      }
    });

    // 再生終了時にaudioManagerをクリア
    audio.addEventListener("ended", () => {
      audioManager.clearAudio();
    });

    // console.log("OK", inputText)
    // if (setOutputText) setOutputText(inputText);
    // audioManagerに登録してから再生
    audioManager.setAudio(audio);
    tryPlayAudio(audio);
  };

  try {
    // 文字列からQueryを作り出す
    const createQuery = async (audioInputText: string) => {
      try {
        let queryJson;
        if (isAWS) {
          const customerId = await getCurrentCustomerId();
          queryJson = await api.dictionary.audioQueryWithDict.mutate({
            customerId,
            speaker: speakerInfo.id[0],
            text: audioInputText,
          });
        } else {
          queryJson = await api.voicevox.audioQuery.mutate({
            speaker: speakerInfo.id[0],
            text: audioInputText,
          });
        }

        if (!queryJson) return { queryJson: null, labdata: null };

        const labdata = getLabData(queryJson as Query);

        return { queryJson, labdata };
      } catch (error) {
        console.error("Error creating query:", error);
        throw error;
      }
    };

    // Queryから合成音声を作り出す
    const createVoice = async (queryJson: Query) => {
      try {
        const result = await api.voicevox.synthesis.mutate({
          speaker: speakerInfo.id[1],
          queryJson: queryJson,
        });

        if (!result) return null;

        // Base64をBlobに変換
        // const byteCharacters = atob(result.audioData);
        const byteArray = Uint8Array.from(atob(result.audioData), (c) => c.charCodeAt(0));
        return new Blob([byteArray], { type: "audio/wav" });
      } catch (error) {
        console.error("Error creating voice:", error);
        throw error;
      }
    };

    // $$key$$ を取り除いたテキストを音声合成に使用
    const audioInputText = removeDollarKeys(inputText);

    const { queryJson, labdata } = await createQuery(audioInputText);
    if (!queryJson || !labdata) return;

    // 出力変えるためにクエリをいじる
    const editedQuery = EditQuery(queryJson as Query, speakerInfo);
    if (!editedQuery) return;

    const audioData = await createVoice(queryJson as Query);
    if (!audioData) return;
    // console.log("editQuery", editedQuery);

    // Web Audio APIを使用して音声を再生する
    const audioUrl = URL.createObjectURL(audioData);

    // 合成中に画面遷移などで別の再生がリクエストされた場合、この音声は古いので
    // 再生もチャット出力も行わずに破棄する
    if (playbackToken !== undefined && !audioManager.isLatestPlayback(playbackToken)) {
      console.info("VoiceVox TTS - Skipping stale audio (playback superseded)");
      URL.revokeObjectURL(audioUrl);
      if (setIsThinking) setIsThinking(false);
      return;
    }

    const audio = new Audio(audioUrl);

    playAndGetLabData(labdata, audio);

    // チャット出力
    if (setOutputText) setOutputText(inputText);

    // audio.play();
  } catch (error) {
    console.error("Error occurred while synthesizing text:", error);
    if (setIsThinking) setIsThinking(false);
    if (setIsProcessing) setIsProcessing(false);
    if (setOutputText) setOutputText(t("audioPlaybackError"));
    if (setIsInConversationCycle) setIsInConversationCycle(false);
    showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
  }
};

export default handleSynthesize;
